"use client"

import { useState } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { User, Calendar } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Link } from "@/i18n/navigation"
import { AnimatedSection } from "../animated-section"

interface Founder {
  id: string
  name: string
  image: string
}

const FOUNDERS: Founder[] = [
  { id: "michael", name: "Michael", image: "/team/Michael.png" },
  { id: "siamak", name: "Siamak", image: "/team/Siamak.png" },
]

function ActionRow() {
  const t = useTranslations("start.founders")
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <Button variant="glass" size="sm" className="rounded-xl" asChild>
        <a href="https://meet.brevo.com/donista-founders" target="_blank" rel="noopener noreferrer">
          <Calendar className="mr-2 h-4 w-4" />
          {t("speakWithFounders.title")}
        </a>
      </Button>
    </div>
  )
}

/** Plain inline team link (not a button, per request) — high-contrast so it reads
 *  clearly on the page's green-tinted gradient. */
function TeamLink() {
  const t = useTranslations("start.founders")
  return (
    <p className="mt-8 text-center text-sm font-medium text-foreground/80">
      {t("teamLink")}{" "}
      <Link href="/about-us/team" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
        {t("teamLinkButton")}
      </Link>
    </p>
  )
}

function useDialog() {
  const t = useTranslations("start.founders")
  const [selected, setSelected] = useState<Founder | null>(null)
  const dialog = (
    <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <User className="h-6 w-6" />
            {selected?.name}, {selected && t(`${selected.id}.role`)}
          </DialogTitle>
        </DialogHeader>
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-6">
              <Image src={selected.image} alt={selected.name} width={160} height={160} className="h-24 w-24 rounded-full border-4 border-primary/20 object-cover" />
              <div>
                <h3 className="mb-2 text-xl font-semibold">{selected.name}</h3>
                <p className="font-medium text-primary">{t(`${selected.id}.role`)}</p>
              </div>
            </div>
            <div>
              <h4 className="mb-3 font-semibold">{t("dialog.motivation")}</h4>
              <p className="italic text-muted-foreground">&quot;{t(`${selected.id}.motivation`)}&quot;</p>
            </div>
            <div>
              <h4 className="mb-3 font-semibold">{t("dialog.career")}</h4>
              <ul className="space-y-2">
                {(Array.isArray(t.raw(`${selected.id}.vita`)) ? (t.raw(`${selected.id}.vita`) as string[]) : []).map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
  return { open: setSelected, dialog }
}

function Heading() {
  const t = useTranslations("start.founders")
  return (
    <AnimatedSection>
      <div className="mb-10 text-center">
        <h2 className="mb-4 text-3xl font-bold">{t("title")}</h2>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{t("subtitle")}</p>
      </div>
    </AnimatedSection>
  )
}

/** row — single row of founder cards: bigger avatar + role + motivation shown inline. */
export function FoundersRow() {
  const t = useTranslations("start.founders")
  const { open, dialog } = useDialog()
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl">
          <Heading />
          <div className="mb-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {FOUNDERS.map((f, index) => (
              <AnimatedSection key={f.id} delay={index * 0.1}>
                <button onClick={() => open(f)} className="flex w-full flex-col items-center gap-2 text-center">
                  <Image src={f.image} alt={f.name} width={200} height={200} className="h-28 w-28 rounded-full object-cover ring-2 ring-primary/20 transition-transform hover:scale-105" />
                  <div>
                    <span className="block text-lg font-semibold">{f.name}</span>
                    <span className="block text-sm text-primary">{t(`${f.id}.role`)}</span>
                  </div>
                  <p className="line-clamp-3 max-w-xs text-sm italic text-muted-foreground">&quot;{t(`${f.id}.motivation`)}&quot;</p>
                </button>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.2}>
            <ActionRow />
          </AnimatedSection>
          <TeamLink />
          {dialog}
        </div>
      </div>
    </section>
  )
}
